import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/StatusBadge";
import { FlowchartCanvas } from "@/components/FlowchartCanvas";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Save, Send, ArrowLeft, PenSquare, Workflow, FileText, ListOrdered, Sparkles, RefreshCw, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { StepsEditor } from "@/components/StepsEditor";

/**
 * Handles both:
 *   /create              — new SOP creation
 *   /sops/:id/edit       — editing the current version (draft only)
 */
export default function SOPEditor() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [meta, setMeta] = useState({
    title: "",
    description: "",
    department: user?.department || "",
    folder_id: "",
    tags: "",
    visibility: "all",
    expiry_days: 365,
    part_numbers: "",
  });

  const [content, setContent] = useState("");
  const [flowchart, setFlowchart] = useState({ nodes: [], edges: [] });
  const [steps, setSteps] = useState([]);
  const [folders, setFolders] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [users, setUsers] = useState([]);
  const [sop, setSop] = useState(null);
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("content");
  const [autofilling, setAutofilling] = useState(false);
  const [autofillSuccess, setAutofillSuccess] = useState(false);
  const autosaveTimer = useRef(null);
  const [savedAt, setSavedAt] = useState(null);

  // Load folders + users
  useEffect(() => {
    (async () => {
      try {
        const [f, u] = await Promise.all([api.get("/folders"), api.get("/users")]);
        setFolders(f.data);
        setUsers(u.data);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  // Load SOP if editing
  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/sops/${id}`);
        setSop(data.sop);
        setVersion(data.current_version);
        setMeta({
          title: data.sop.title,
          description: data.sop.description || "",
          department: data.sop.department || "",
          folder_id: data.sop.folder_id || "",
          tags: (data.sop.tags || []).join(", "),
          visibility: data.sop.visibility || "all",
          expiry_days: 365,
          part_numbers: (data.sop.part_numbers || []).join(", "),
        });
        setContent(data.current_version?.content || "");
        setFlowchart(data.current_version?.flowchart || { nodes: [], edges: [] });
        setSteps(data.current_version?.steps || []);
      } catch (e) {
        setErr(formatApiError(e?.response?.data?.detail) || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const canEdit = useMemo(() => {
    if (isNew) return true;
    if (!sop || !user) return false;
    if (sop.status !== "draft") return false;
    return user.role === "admin" || sop.owner_id === user.id || sop.created_by === user.id;
  }, [isNew, sop, user]);

  const saveMeta = async () => {
    setErr("");
    setSaving(true);
    try {
      if (isNew) {
        const payload = {
          title: meta.title,
          description: meta.description,
          folder_id: meta.folder_id || null,
          department: meta.department || null,
          tags: meta.tags.split(",").map((t) => t.trim()).filter(Boolean),
          part_numbers: meta.part_numbers.split(",").map((t) => t.trim()).filter(Boolean),
          visibility: meta.visibility,
          expiry_days: Number(meta.expiry_days) || null,
        };
        const { data } = await api.post("/sops", payload);

        if (content && data.current_version_id) {
          await api.patch(`/sops/${data.id}/versions/${data.current_version_id}`, {
            content,
            flowchart,
            steps,
          });
        }

        setSavedAt(new Date());
        navigate(`/sops/${data.id}/edit`, { replace: true });
      } else {
        const payload = {
          title: meta.title,
          description: meta.description,
          folder_id: meta.folder_id || null,
          department: meta.department || null,
          tags: meta.tags.split(",").map((t) => t.trim()).filter(Boolean),
          part_numbers: meta.part_numbers.split(",").map((t) => t.trim()).filter(Boolean),
          visibility: meta.visibility,
        };
        await api.patch(`/sops/${id}`, payload);
        setSavedAt(new Date());
      }
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveVersion = async (silent = false) => {
    if (isNew || !version) return;
    if (!silent) setSaving(true);
    try {
      await api.patch(`/sops/${id}/versions/${version.id}`, {
        content,
        flowchart,
        steps,
      });
      setSavedAt(new Date());
    } catch (e) {
      if (!silent) setErr(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      if (!silent) setSaving(false);
    }
  };

  // Autosave for content & flowchart
  useEffect(() => {
    if (isNew || !version) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => saveVersion(true), 1500);
    return () => clearTimeout(autosaveTimer.current);
  }, [content, flowchart, steps]);

  const submitForReview = async () => {
    if (!version) return;
    if (approvers.length === 0) {
      setErr("Select at least one approver.");
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      await saveVersion(true);
      await api.post(`/sops/${id}/versions/${version.id}/submit`, {
        approver_ids: approvers,
        reviewer_ids: [],
        sequential: false,
        changelog: version.changelog || "",
      });
      navigate(`/sops/${id}`);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };


  // Simulate file upload autofill via AI (with active progress spinner)
  const triggerAiAutofill = (type) => {
    setAutofilling(true);
    setAutofillSuccess(false);

    setTimeout(() => {
      if (type === "cleanroom") {
        setMeta((m) => ({
          ...m,
          title: "Laminar Flow Hood Safety Protocol",
          description: "Structured cleanroom airlock sanitization checklist for Class A facilities.",
          department: "IT & Security",
          tags: "semiconductor, cleanroom, airlock",
          visibility: "all",
          expiry_days: 365,
        }));
        setContent("<h2>Laminar Flow Hood Safety Protocol</h2><h3>1. Airflow Controls</h3><p>Verify that the vacuum gauges are active, showing green status, and maintaining laminate airflow standards.</p><h3>2. Sanitization</h3><p>Ensure Class A micro-filters are wiped hourly using 70% Isopropyl Alcohol solution.</p>");
      } else {
        setMeta((m) => ({
          ...m,
          title: "Enterprise Expense Travel Matching SOP",
          description: "Procedure for matching daily ledger receipts and bank reconciliations.",
          department: "Finance",
          tags: "audit, stripe, finance",
          visibility: "all",
          expiry_days: 365,
        }));
        setContent("<h2>Enterprise Expense Travel Matching SOP</h2><h3>1. Matching Cycle</h3><p>Compare travel receipts against Stripe transactions daily to verify currency and amount matching.</p><h3>2. Approvals</h3><p>Forward mismatch records exceeding $50.00 to the Finance department head for review.</p>");
      }
      setAutofilling(false);
      setAutofillSuccess(true);
      setTimeout(() => setAutofillSuccess(false), 3000);
    }, 1200);
  };

  if (loading) {
    return (
      <div className="pt-10 flex justify-center">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2563EB] to-[#4F46E5] animate-pulse-glow" />
      </div>
    );
  }

  const ownerApproverCandidates = users.filter((u) => u.role === "owner" || u.role === "admin");

  return (
    <div className="space-y-6" data-testid="sop-editor-page">
      <header className="flex items-start justify-between gap-3">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-xs text-[#9AA4B2] hover:text-white flex items-center gap-1"
            data-testid="editor-back"
          >
            <ArrowLeft size={12} /> Back
          </button>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-white">
            {isNew ? "Draft a new SOP" : "Edit SOP"}
          </h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-[#9AA4B2]">
            {sop && <StatusBadge status={sop.status} />}
            {version && <span>v{version.version_number}</span>}
            {savedAt && <span className="text-[11px] text-emerald-400">Saved {savedAt.toLocaleTimeString()}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {isNew && (
            <Button
              onClick={saveMeta}
              disabled={saving || !meta.title.trim()}
              className="btn-primary-glow rounded-full px-5"
              data-testid="editor-create-btn"
            >
              <PenSquare size={14} className="mr-1.5" /> Create draft
            </Button>
          )}
          {!isNew && canEdit && (
            <>
              <Button
                onClick={() => saveVersion(false)}
                disabled={saving}
                variant="ghost"
                className="btn-ghost-glass rounded-full px-5"
                data-testid="editor-save-btn"
              >
                <Save size={14} className="mr-1.5" /> Save
              </Button>
              <Button
                onClick={submitForReview}
                disabled={submitting || approvers.length === 0}
                className="btn-primary-glow rounded-full px-5"
                data-testid="editor-submit-btn"
              >
                <Send size={14} className="mr-1.5" /> Submit for review
              </Button>
            </>
          )}
        </div>
      </header>

      {err && (
        <GlassCard className="p-4 border border-rose-500/30 bg-rose-500/5">
          <div className="text-sm text-rose-300" data-testid="editor-error">{err}</div>
        </GlassCard>
      )}


      {/* AI File Autofill Trigger block inside create view */}
      {isNew && (
        <GlassCard className="p-5 border border-cyan-500/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-cyan-500/5 rounded-full blur-3xl animate-pulse" />

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative">
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-1.5">
                <Sparkles size={16} className="text-cyan-300" />
                AI File Autofill Extractor
              </h3>
              <p className="text-xs text-[#9AA4B2] mt-1 max-w-xl">
                Autofill all procedural fields instantly by simulating an AI PDF or document scan. The assistant extracts titles, categories, descriptions, and structures the draft SOP.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => triggerAiAutofill("cleanroom")}
                disabled={autofilling}
                className="btn-ghost-glass text-xs rounded-full px-4 py-2"
              >
                {autofilling ? <RefreshCw className="animate-spin mr-1" size={12} /> : <FileText size={12} className="mr-1" />}
                Scan Cleanroom PDF
              </Button>
              <Button
                type="button"
                onClick={() => triggerAiAutofill("expense")}
                disabled={autofilling}
                className="btn-ghost-glass text-xs rounded-full px-4 py-2"
              >
                {autofilling ? <RefreshCw className="animate-spin mr-1" size={12} /> : <FileText size={12} className="mr-1" />}
                Scan Financial DOCX
              </Button>
            </div>
          </div>

          {autofillSuccess && (
            <div className="mt-3 p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300 flex items-center gap-2">
              <CheckCircle2 size={14} /> AI file scanning complete. Metadata and rich text fields pre-filled below!
            </div>
          )}
        </GlassCard>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left: content / flowchart */}
        <div className="lg:col-span-2 space-y-4">
          <GlassCard className="p-5">
            <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Title</label>
            <Input
              data-testid="editor-title"
              value={meta.title}
              onChange={(e) => setMeta({ ...meta, title: e.target.value })}
              onBlur={() => !isNew && saveMeta()}
              placeholder="Employee Onboarding Checklist"
              className="mt-1 bg-transparent border-0 border-b border-white/10 rounded-none px-0 focus-visible:border-cyan-400 focus-visible:ring-0 text-2xl text-white font-display"
            />
            <label className="mt-4 block text-xs uppercase tracking-widest text-[#9AA4B2]">Description</label>
            <Textarea
              data-testid="editor-description"
              value={meta.description}
              onChange={(e) => setMeta({ ...meta, description: e.target.value })}
              onBlur={() => !isNew && saveMeta()}
              placeholder="One-sentence summary of this procedure."
              className="mt-1 bg-white/[0.04] border-white/10 rounded-2xl text-white placeholder:text-[#5B6472]"
              rows={2}
            />
          </GlassCard>

          {!isNew && (
            <GlassCard className="p-5">
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-white/[0.04] border border-white/10 rounded-full flex-wrap h-auto gap-1 p-1">
                  <TabsTrigger value="content" data-testid="tab-content" className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#2563EB]/60 data-[state=active]:to-[#4F46E5]/60 data-[state=active]:text-white">
                    <FileText size={14} className="mr-1.5" /> Content
                  </TabsTrigger>
                  <TabsTrigger value="steps" data-testid="tab-steps" className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#2563EB]/60 data-[state=active]:to-[#4F46E5]/60 data-[state=active]:text-white">
                    <ListOrdered size={14} className="mr-1.5" /> Steps
                  </TabsTrigger>
                  <TabsTrigger value="flowchart" data-testid="tab-flowchart" className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#2563EB]/60 data-[state=active]:to-[#4F46E5]/60 data-[state=active]:text-white">
                    <Workflow size={14} className="mr-1.5" /> Flowchart
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="content" className="mt-4">
                  <div className="text-[11px] text-[#5B6472] mb-2">Simple HTML content — use &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;strong&gt; for structure.</div>
                  <Textarea
                    data-testid="editor-content"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder={"<h2>Step 1</h2>\n<p>Do this…</p>"}
                    className="min-h-[300px] bg-white/[0.03] border-white/10 rounded-2xl text-white placeholder:text-[#5B6472] font-mono text-sm"
                  />
                  <div className="mt-3 text-xs uppercase tracking-widest text-[#9AA4B2]">Live preview</div>
                  <div
                    className="mt-2 p-4 rounded-2xl border border-white/10 bg-white/[0.02] prose-sop"
                    dangerouslySetInnerHTML={{ __html: content || "<p class='text-[#5B6472]'>Nothing yet — start typing above.</p>" }}
                  />
                </TabsContent>
                <TabsContent value="steps" className="mt-4">
                  <StepsEditor sopId={id} versionId={version?.id} steps={steps} setSteps={setSteps} />
                </TabsContent>
                <TabsContent value="flowchart" className="mt-4">
                  <FlowchartCanvas initial={flowchart} onChange={setFlowchart} />
                </TabsContent>
              </Tabs>
            </GlassCard>
          )}
        </div>

        {/* Right: meta + approvers */}
        <div className="space-y-4">
          <GlassCard className="p-5">
            <h3 className="font-display text-white mb-3">Organisation</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Folder</label>
                <select
                  data-testid="editor-folder"
                  value={meta.folder_id}
                  onChange={(e) => setMeta({ ...meta, folder_id: e.target.value })}
                  onBlur={() => !isNew && saveMeta()}
                  className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-2xl px-3 py-2.5 text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="">Unfiled</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Department</label>
                <Input
                  data-testid="editor-department"
                  value={meta.department}
                  onChange={(e) => setMeta({ ...meta, department: e.target.value })}
                  onBlur={() => !isNew && saveMeta()}
                  placeholder="Operations"
                  className="mt-1 bg-white/[0.04] border-white/10 rounded-2xl text-white"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Tags (comma-separated)</label>
                <Input
                  data-testid="editor-tags"
                  value={meta.tags}
                  onChange={(e) => setMeta({ ...meta, tags: e.target.value })}
                  onBlur={() => !isNew && saveMeta()}
                  placeholder="onboarding, hr"
                  className="mt-1 bg-white/[0.04] border-white/10 rounded-2xl text-white"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Part numbers (resolve API)</label>
                <Input
                  data-testid="editor-part-numbers"
                  value={meta.part_numbers}
                  onChange={(e) => setMeta({ ...meta, part_numbers: e.target.value })}
                  onBlur={() => !isNew && saveMeta()}
                  placeholder="ABC-123, XYZ-9"
                  className="mt-1 bg-white/[0.04] border-white/10 rounded-2xl text-white"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Visibility</label>
                <select
                  data-testid="editor-visibility"
                  value={meta.visibility}
                  onChange={(e) => setMeta({ ...meta, visibility: e.target.value })}
                  onBlur={() => !isNew && saveMeta()}
                  className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-2xl px-3 py-2.5 text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="all">All users</option>
                  <option value="department">Department only</option>
                  <option value="custom">Selected users</option>
                </select>
              </div>
              {isNew && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Expiry (days)</label>
                  <Input
                    type="number"
                    value={meta.expiry_days}
                    onChange={(e) => setMeta({ ...meta, expiry_days: e.target.value })}
                    className="mt-1 bg-white/[0.04] border-white/10 rounded-2xl text-white"
                  />
                </div>
              )}
            </div>
          </GlassCard>

          {!isNew && canEdit && (
            <GlassCard className="p-5">
              <h3 className="font-display text-white mb-3">Approvers</h3>
              <p className="text-xs text-[#9AA4B2] mb-3">
                Pick one or more Process Owners to review and sign off. Parallel approval — all must approve.
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {ownerApproverCandidates.map((u) => (
                  <label key={u.id} className="flex items-center gap-3 p-2 rounded-2xl hover:bg-white/[0.04] cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`approver-${u.id}`}
                      checked={approvers.includes(u.id)}
                      onChange={(e) => {
                        setApprovers((prev) =>
                          e.target.checked ? [...prev, u.id] : prev.filter((x) => x !== u.id)
                        );
                      }}
                      className="accent-indigo-500"
                    />
                    <div>
                      <div className="text-sm text-white">{u.name}</div>
                      <div className="text-[11px] text-[#5B6472]">{u.department} · {u.role}</div>
                    </div>
                  </label>
                ))}
              </div>
            </GlassCard>
          )}

          {!isNew && (
            <GlassCard className="p-5">
              <h3 className="font-display text-white mb-2">Version notes</h3>
              <Textarea
                data-testid="editor-changelog"
                value={version?.changelog || ""}
                onChange={(e) => setVersion({ ...version, changelog: e.target.value })}
                onBlur={() =>
                  api.patch(`/sops/${id}/versions/${version.id}`, { changelog: version?.changelog || "" }).catch(() => {})
                }
                placeholder="What changed in this revision?"
                className="bg-white/[0.04] border-white/10 rounded-2xl text-white placeholder:text-[#5B6472]"
                rows={3}
              />
            </GlassCard>
          )}
        </div>
      </div>
    </div>
  );
}
